import { useId, useState } from "react";
import { Check, Download } from "lucide-react";
import type { Step } from "../../shared/types.ts";

type ContactCard = NonNullable<Step["saveCard"]>;

const phoneNumber = /^\+?[\d\s().-]{7,}$/;
const vcardText = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/[,;]/g, (match) => "\\" + match);

/** vCard 3.0 built only from the authored card: phone rows become numbers, the rest a note. */
export function contactCard(card: ContactCard): string {
  const phones = card.rows.filter((row) => phoneNumber.test(row.value.trim()));
  const notes = card.rows
    .filter((row) => !phones.includes(row))
    .map((row) => row.label + ": " + row.value);
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    "FN:" + vcardText(card.title),
    "N:" + vcardText(card.title) + ";;;;",
    ...phones.map((row) => "TEL;TYPE=VOICE:" + row.value.trim()),
    ...(notes.length ? ["NOTE:" + vcardText(notes.join("\n"))] : []),
    "END:VCARD",
    "",
  ].join("\r\n");
}

/** Saving is local to this device and transient; it never touches session state. */
export function SaveContactCard({
  card,
  disabled,
}: {
  card: ContactCard;
  disabled: boolean;
}) {
  const titleId = useId();
  const [saved, setSaved] = useState(false);
  const save = () => {
    const url = URL.createObjectURL(
      new Blob([contactCard(card)], { type: "text/vcard;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download =
      (card.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "contact") + ".vcf";
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    setSaved(true);
  };
  return (
    <section className="pe-savecard" aria-labelledby={titleId}>
      <p className="pe-savecard__label">{card.label}</p>
      <h3 id={titleId} className="pe-savecard__title">
        {card.title}
      </h3>
      <dl className="pe-savecard__rows">
        {card.rows.map((row, index) => (
          <div key={index} className="pe-savecard__row">
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      <button
        type="button"
        className="pe-btn pe-btn--secondary"
        data-saved={saved || undefined}
        disabled={disabled}
        onClick={save}
      >
        {saved ? (
          <Check size={18} aria-hidden="true" />
        ) : (
          <Download size={18} aria-hidden="true" />
        )}
        <span>{saved ? card.savedLabel : card.cta}</span>
      </button>
      <span className="pe-sr" role="status">
        {saved ? card.savedLabel : ""}
      </span>
    </section>
  );
}
